import React, { createContext, useContext, useMemo, ReactNode } from "react";
import { useMediaQuery } from "@mantine/hooks";

interface ResponsiveContextType {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  breakpoints: {
    mobile: number;
    tablet: number;
    desktop: number;
  };
}

const ResponsiveContext = createContext<ResponsiveContextType | undefined>(
  undefined,
);

interface ResponsiveProviderProps {
  children: ReactNode;
}

// Breakpoint definitions. Module scope so the object identity is stable and
// the memoized context value below doesn't churn on it.
const breakpoints = {
  mobile: 768,
  tablet: 992,
  desktop: 993,
};

export function ResponsiveProvider({ children }: ResponsiveProviderProps) {
  // Media queries for each breakpoint
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.mobile}px)`);
  const isTablet = useMediaQuery(
    `(min-width: ${breakpoints.mobile + 1}px) and (max-width: ${breakpoints.tablet}px)`,
  );
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.desktop}px)`);

  // Memoized so consumers don't re-render on every provider render.
  const value: ResponsiveContextType = useMemo(
    () => ({
      isMobile,
      isTablet,
      isDesktop,
      breakpoints,
    }),
    [isMobile, isTablet, isDesktop],
  );

  return (
    <ResponsiveContext.Provider value={value}>
      {children}
    </ResponsiveContext.Provider>
  );
}

export function useResponsive(): ResponsiveContextType {
  const context = useContext(ResponsiveContext);
  if (context === undefined) {
    throw new Error("useResponsive must be used within a ResponsiveProvider");
  }
  return context;
}
